import { z } from 'zod';
import { MoneySchema } from './common.js';
import { DateOnlySchema, InvoiceCustomerSchema, InvoiceIssuerSchema } from './invoices.js';

/**
 * Monthly statements (docs/28 B3; doc 15 §8).
 *
 * What a client owed at the start of a month; what was invoiced, credited and paid
 * in it; what it owed at the end; the invoices still unpaid then; and what happened
 * to its airtime. Calendar months in Africa/Johannesburg, whatever the billing day.
 */

export const StatementMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month: YYYY-MM');

export const StatementLineSchema = z.object({
  date: DateOnlySchema,
  kind: z.enum(['invoice', 'credit_note', 'payment']),
  reference: z.string().nullable(),
  description: z.string(),
  /** Positive adds to what is owed; negative takes from it. */
  amount: MoneySchema,
  /** What was owed after this line. */
  balance: MoneySchema,
});
export type StatementLine = z.infer<typeof StatementLineSchema>;

export const StatementUnpaidSchema = z.object({
  number: z.string(),
  issue_date: DateOnlySchema,
  due_date: DateOnlySchema,
  /** Still owed at the end of the month, as it stood then. */
  amount_due: MoneySchema,
  /** Past its due date by the month's last day. */
  overdue: z.boolean(),
});

export const AIRTIME_MOVEMENT_KINDS = [
  'topup', 'call_charge', 'adjustment', 'invoice', 'refund', 'monthly_fee', 'reversal',
] as const;

export const StatementAirtimeSchema = z.object({
  opening: MoneySchema,
  closing: MoneySchema,
  movements: z.array(z.object({
    kind: z.enum(AIRTIME_MOVEMENT_KINDS),
    count: z.number().int(),
    amount: MoneySchema,
  })),
});

export const StatementSchema = z.object({
  month: StatementMonthSchema,
  period_start: DateOnlySchema,
  /** The month's last day. */
  period_end: DateOnlySchema,
  currency: z.string().length(3),
  /** Null for a client its reseller bills (B4). */
  issuer: InvoiceIssuerSchema.nullable(),
  customer: InvoiceCustomerSchema,
  /** Owed at the start of the month. */
  opening: MoneySchema,
  lines: z.array(StatementLineSchema),
  /** Owed at the end of the month. */
  closing: MoneySchema,
  unpaid: z.array(StatementUnpaidSchema),
  /** Null for an account that had no airtime movement by the end of the month. */
  airtime: StatementAirtimeSchema.nullable(),
});
export type Statement = z.infer<typeof StatementSchema>;

/** The months a statement can be read for, the latest first. */
export const StatementMonthsSchema = z.object({ data: z.array(StatementMonthSchema) });
export type StatementMonths = z.infer<typeof StatementMonthsSchema>;
